import * as React from 'react';
import { observer } from 'mobx-react';
import { ProrationRule } from 'nomst/Stores/Configuration/prorationRule';
import { ProrationRuleFilter } from 'nomst/Stores/Configuration/prorationRuleFilter';
import { RuleCard } from 'nomst/Components/EligibilityProration/ruleCard';
import { ProrateMethod, ProrationRuleFilters } from 'typings/enums';
import { FormGroup, Input, Options, Alert, Option } from '@payscale/design';
import { formatDate, parseDate, getDateLimitForCycle } from 'utils/dateUtils';
import { CustomDatePicker } from 'nomst/Components/Utils/customDatePicker';
import './prorationRuleCard.scss';

const calendarDurationUnits: {[key in ProrateMethod]: string} = {
    [ProrateMethod.Daily]: 'Days',
    [ProrateMethod.Weekly]: 'Weeks',
    [ProrateMethod.Monthly]: 'Months'
};

export interface IProrationRuleCardProps {
    rule: ProrationRule;
    removeRule: (rule: ProrationRule) => void;
    handleDeleteClick: (rule: ProrationRule) => void;
    updateOtherRuleAmounts: (rule: ProrationRule) => void;
}

export class ProrationRuleCard extends React.Component<IProrationRuleCardProps> {
    constructor(props: IProrationRuleCardProps) {
        super(props);

        this.handleBudgetFieldsChange = this.handleBudgetFieldsChange.bind(this);
        this.handleHasHireDateFilterChange = this.handleHasHireDateFilterChange.bind(this);
        this.handleStartDateChange = this.handleStartDateChange.bind(this);
        this.handleEndDateChange = this.handleEndDateChange.bind(this);
        this.handleHasPayEffectiveFilterChange = this.handleHasPayEffectiveFilterChange.bind(this);
        this.handleProrateMethodChange = this.handleProrateMethodChange.bind(this);
        this.handleCalendarDurationChange = this.handleCalendarDurationChange.bind(this);
        this.handleDaysInMonthChange = this.handleDaysInMonthChange.bind(this);
        this.handleDaysInWeekChange = this.handleDaysInWeekChange.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.renderSavedRuleFilter = this.renderSavedRuleFilter.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const { rule } = this.props;
        if (!rule.isEditMode) {
            await rule.getEmployeeCount();
        }
    }

    render(): React.ReactNode {
        const { rule } = this.props;
        const body = rule.isEditMode ? this.renderEditRule() : this.renderSavedRule();
        const employeeCount = rule.isLoadingCount
            ? <i className="icon-animate-spin icon-spin" />
            : rule.employeeCount;
        const footer = (
            <>
                <p>Employees within this rule: {employeeCount}</p>
                {this.renderFooterButtons()}
            </>
        );

        return (
            <RuleCard
                mainContent={body}
                footerContent={footer}
                className="proration-rule-card"
            />
        );
    }

    private renderSavedRule(): React.ReactNode {
        const { rule } = this.props;
        const methodText = this.renderRuleCategory(rule.prorateMethod);
        const calendarDurationText = (rule.calendarDuration && rule.prorateMethod)
            ? `${rule.calendarDuration} ${calendarDurationUnits[rule.prorateMethod]}`
            : '';
        const calendarDuration = this.renderRuleCategory(calendarDurationText);

        return (
            <div className="proration-rule-card__content proration-rule-card__content--readonly">
                <h3 className="proration-rule-card__title">
                    Employees will be prorated for:
                </h3>
                <p className="proration-rule-card__filter">
                    <span className="proration-rule-card__filter-category">
                        {rule.associatedAmountsTitle.join(', ')}
                    </span> Budget
                </p>
                {this.renderSavedRuleFilter(rule.ruleFilter)}
                <p className="proration-rule-card__filter">
                    When Proration Method is {methodText} and Calendar Duration is {calendarDuration}
                </p>
            </div>
        );
    }

    private renderSavedRuleFilter(ruleFilter: ProrationRuleFilter): React.ReactNode {
        return (
            <p
                key={ruleFilter.ruleCategoryId}
                className="proration-rule-card__filter"
            >
                If {this.renderRuleCategory(ruleFilter.ruleCategoryName)} is
                within {formatDate(ruleFilter.ruleCategoryStartValue)} and {formatDate(ruleFilter.ruleCategoryEndValue)}
            </p>
        );
    }

    private renderRuleCategory(category: Nullable<string>): React.ReactNode {
        if (!category) {
            return null;
        }

        return (
            <span className="proration-rule-card__filter-category">
                {category}
            </span>
        );
    }

    private renderEditRule(): React.ReactNode {
        const { rule } = this.props;
        return (
            <div className="proration-rule-card__content">
                <h3 className="proration-rule-card__title">
                    Employees will be prorated for:
                </h3>
                <FormGroup>
                    <label htmlFor="proration-rule-card__budgets">
                        Select Budget:
                    </label>
                    <Options
                        id="proration-rule-card__budgets"
                        options={rule.currentCycleAmountsTitle}
                        onChange={this.handleBudgetFieldsChange}
                        syncOptions={false}
                        value={[...rule.editableAssociatedAmountsTitle]}
                        type="pills"
                        horizontal={true}
                        optionProps={{'className': 'pxl-pill--secondary'}}
                    />
                    {this.renderBudgetAlert()}
                    {this.renderBudgetUsingOtherProrationRuleAlert()}
                </FormGroup>
                {this.renderDateFilters()}
                {this.renderProrateMethod()}
            </div>
        );
    }

    private renderDateFilters(): React.ReactNode {
        const { rule } = this.props;
        const maxDateLimt = getDateLimitForCycle(true, 100);
        const minDateLimit = getDateLimitForCycle(false, 100);
        const hasHireDateFilter = rule.hasRuleFilter(ProrationRuleFilters.HireDate);
        const hasPayEffectiveDateFilter = rule.hasRuleFilter(ProrationRuleFilters.PayEffectiveDate);

        return (
            <div className="proration-rule-card__date-filters">
                <p className="proration-rule-card__date-label">
                    Select date to prorate:
                </p>
                <FormGroup>
                    <Option
                        id="proration-rule-card__hire-date-checkbox"
                        checked={hasHireDateFilter}
                        onChange={this.handleHasHireDateFilterChange}
                        text="Hire Date is within"
                        className="pxl-option--secondary"
                    />
                    <div className="proration-rule-card__date-inputs">
                        <CustomDatePicker
                            id="proration-hire-date-start"
                            value={parseDate(rule.getEditableFilterStartValue(ProrationRuleFilters.HireDate))}
                            onChange={this.handleStartDateChange}
                            disabled={!hasHireDateFilter}
                            clearIcon={null}
                            maxDateValue={maxDateLimt}
                            minDateValue={minDateLimit}
                        />
                        <p className="proration-rule-card__date-between">
                            and
                        </p>
                        <CustomDatePicker
                            id="proration-hire-date-end"
                            value={parseDate(rule.getEditableFilterEndValue(ProrationRuleFilters.HireDate))}
                            onChange={this.handleEndDateChange}
                            disabled={!hasHireDateFilter}
                            clearIcon={null}
                            maxDateValue={maxDateLimt}
                            minDateValue={minDateLimit}
                        />
                    </div>
                </FormGroup>
                <FormGroup className="proration-rule-card__pay-effective-date">
                    <Option
                        id="proration-rule-card__pay-effective-checkbox"
                        checked={hasPayEffectiveDateFilter}
                        onChange={this.handleHasPayEffectiveFilterChange}
                        text="Pay Effective Date is within"
                        className="pxl-option--secondary"
                    />
                    <div className="proration-rule-card__date-inputs">
                        <CustomDatePicker
                            id="proration-pay-effective-date-start"
                            value={parseDate(rule.getEditableFilterStartValue(ProrationRuleFilters.PayEffectiveDate))}
                            onChange={this.handleStartDateChange}
                            disabled={!hasPayEffectiveDateFilter}
                            clearIcon={null}
                            maxDateValue={maxDateLimt}
                            minDateValue={minDateLimit}
                        />
                        <p className="proration-rule-card__date-between">
                            and
                        </p>
                        <CustomDatePicker
                            id="proration-pay-effective-date-end"
                            value={parseDate(rule.getEditableFilterEndValue(ProrationRuleFilters.PayEffectiveDate))}
                            onChange={this.handleEndDateChange}
                            disabled={!hasPayEffectiveDateFilter}
                            clearIcon={null}
                            maxDateValue={maxDateLimt}
                            minDateValue={minDateLimit}
                        />
                    </div>
                </FormGroup>
                {hasPayEffectiveDateFilter && <p className="proration-rule-card__fall-back-text">
                    Hire Date will be used if Pay Effective Date is unavailable
                </p>}
                <p className="proration-rule-card__example">
                    Example: a date range of 1 year of proration would be 1/1/2019 - 12/31/2019
                </p>
            </div>
        );
    }

    private renderProrateMethod(): React.ReactNode {
        const { rule } = this.props;
        return (
            <>
                <div className="proration-rule-card__method-duration">
                    <FormGroup className="proration-rule-card__method">
                        <label htmlFor="prorate-method-options">
                            Proration Method:
                        </label>
                        <Options
                            id="prorate-method-options"
                            type="radio"
                            multiValue={false}
                            options={['Daily', 'Weekly', 'Monthly']}
                            value={rule.editableProrateMethod}
                            onChange={this.handleProrateMethodChange}
                            syncOptions={false}
                            optionProps={{'className': 'pxl-option--secondary'}}
                        />
                    </FormGroup>
                    <FormGroup className="proration-rule-card__duration">
                        <label htmlFor="calendar-duration">
                            Calendar Duration
                        </label>
                        <Input
                            id="calendar-duration"
                            className="proration-rule-card__calendar-duration"
                            type="number"
                            value={String(rule.editableCalendarDuration)}
                            onChange={this.handleCalendarDurationChange}
                        />
                        <p className="proration-rule-card__units">Days</p>
                    </FormGroup>
                </div>
                {this.renderNumberOfDaysForm()}
            </>
        );
    }

    private renderNumberOfDaysForm(): React.ReactNode {
        const { rule } = this.props;
        if (!rule.shouldShowNumberOfDaysForm) {
            return null;
        }

        let text = 'Number of days an employee should have worked in a given calendar month to include that month';
        let value = rule.editableNumberOfDaysInAMonth;
        let handleNumberOfDaysChange = this.handleDaysInMonthChange;
        if (rule.editableProrateMethod === ProrateMethod.Weekly) {
            text = 'Number of days an employee should have worked in a given week to include that week';
            value = rule.editableNumberOfDaysInAWeek;
            handleNumberOfDaysChange = this.handleDaysInWeekChange;
        }

        return (
            <FormGroup className="proration-rule-card__number-of-days">
                <label htmlFor="number-of-days">
                    {text}
                </label>
                <Input
                    id="number-of-days"
                    className="proration-rule-card__days-input"
                    type="number"
                    value={String(value)}
                    onChange={handleNumberOfDaysChange}
                />
                <p className="proration-rule-card__units">Days</p>
            </FormGroup>
        );
    }

    private renderBudgetAlert(): React.ReactNode {
        const usedBudgetFields = this.props.rule.cycleAmountsWithNoBudgetOrBudgetNotUsingProration;
        if (!usedBudgetFields.length) {
            return null;
        }

        const messageText = ' is associated with another rule. It will be moved to this one.';
        return usedBudgetFields.map(budgetField => {
            const message = (
                <p>
                    <span className="proration-rule-card__budget-title">
                        {budgetField.title}
                    </span>
                    {messageText}
                </p>
            );

            return (
                <Alert
                    key={budgetField.title}
                    className="proration-rule-card__budget-alert"
                    message={message}
                    level="warning"
                    close={false}
                />
            );
        });
    }

    private renderBudgetUsingOtherProrationRuleAlert(): React.ReactNode {
        const { rule } = this.props;
        const budgetFields = rule.cycleAmountsWithBudgetUsingOtherProrationRule;
        if (!rule.isEditMode || !budgetFields.length) {
            return null;
        }

        return budgetFields.map(budgetField => {
            const message = `${budgetField.title} has budget using another proration rule. ` +
                `${budgetField.title} cannot be chosen in this proration rule.`;
            return (
                <Alert
                    key={budgetField.title}
                    className="proration-rule-card__budget-alert"
                    message={message}
                    level="danger"
                    close={false}
                />
            );
        });
    }

    private renderFooterButtons(): React.ReactNode {
        const { rule } = this.props;
        if (rule.isEditMode) {
            const saveBtnText = rule.isSaving ? 'Saving...' : 'Save';
            return (
                <div className="proration-rule-card__footer-btns">
                    <button
                        className="pxl-link proration-rule-card__cancel-btn"
                        type="button"
                        onClick={this.handleCancelClick}
                    >
                        Cancel
                    </button>
                    <button
                        className="pxl-btn pxl-btn--primary proration-rule-card__save-btn"
                        type="button"
                        disabled={rule.isEditInvalid || rule.isSaving}
                        onClick={this.handleSaveClick}
                    >
                        {saveBtnText}
                    </button>
                </div>
            );
        }

        return (
            <div className="proration-rule-card__footer-btns">
                <button
                    className="pxl-link proration-rule-card__delete-btn"
                    type="button"
                    onClick={this.handleDeleteClick}
                >
                    Delete Rule
                </button>
                <button
                    className="pxl-btn pxl-btn--primary proration-rule-card__edit-btn"
                    type="button"
                    onClick={this.handleEditClick}
                >
                    Edit
                </button>
            </div>
        );
    }

    private handleBudgetFieldsChange(_: undefined, value: string[]): void {
        this.props.rule.handleEditableAssociatedAmountsChange(value);
    }

    private handleHasHireDateFilterChange(): void {
        const { rule } = this.props;
        if (rule.hasRuleFilter(ProrationRuleFilters.HireDate)) {
            rule.removeRuleFilter();
        } else {
            rule.addRuleFilter(ProrationRuleFilters.HireDate);
        }
    }

    private handleHasPayEffectiveFilterChange(): void {
        const { rule } = this.props;
        if (rule.hasRuleFilter(ProrationRuleFilters.PayEffectiveDate)) {
            rule.removeRuleFilter();
        } else {
            rule.addRuleFilter(ProrationRuleFilters.PayEffectiveDate);
        }
    }

    private handleStartDateChange(value: Date): Promise<void> {
        return this.props.rule.setFilterStartValue(formatDate(value));
    }

    private handleEndDateChange(value: Date): Promise<void> {
        return this.props.rule.setFilterEndValue(formatDate(value));
    }

    private handleProrateMethodChange(_: undefined, value: ProrateMethod): void {
        this.props.rule.setEditableProrateMethod(value);
    }

    private handleCalendarDurationChange(_: undefined, value: string): void {
        const valueToSave = value ? Number(value) : null;
        this.props.rule.setEditableCalendarDuration(valueToSave);
    }

    private handleDaysInMonthChange(_: undefined, value: string): void {
        this.props.rule.setEditableNumberOfDaysInAMonth(Number(value));
    }

    private handleDaysInWeekChange(_: undefined, value: string): void {
        this.props.rule.setEditableNumberOfDaysInAWeek(Number(value));
    }

    private async handleCancelClick(): Promise<void> {
        const { rule, removeRule } = this.props;
        if (!rule.prorationRuleId) {
            removeRule(rule);
        } else {
            await rule.cancelEditRule();
        }
    }

    private async handleSaveClick(): Promise<void> {
        const { rule, updateOtherRuleAmounts } = this.props;
        await rule.saveChanges();
        updateOtherRuleAmounts(rule);
    }

    private handleEditClick(): void {
        this.props.rule.editRule();
    }

    private async handleDeleteClick(): Promise<void> {
        const { handleDeleteClick, rule } = this.props;
        handleDeleteClick(rule);
    }
}

export default observer(ProrationRuleCard);